/**
 * Build 10 (Daniel 2026-09-08): client for /api/reports. Shapes mirror
 * apps/api/src/lib/reports.ts; money is in cents, format with formatMoney().
 */
import { apiFetch } from '../auth/api';
import type { TxType } from './transactions';

export type ReportPreset =
  | 'last_30' | 'last_60' | 'last_90'
  | 'this_month' | 'last_month' | 'qtd' | 'ytd' | 'last_year' | 'all_time' | 'custom';
export type ReportDateField = 'close' | 'contract' | 'list';
export type ReportCompare = 'none' | 'previous' | 'year_ago';

export const PRESET_LABELS: Record<ReportPreset, string> = {
  last_30: 'Last 30 days',
  last_60: 'Last 60 days',
  last_90: 'Last 90 days',
  this_month: 'This month',
  last_month: 'Last month',
  qtd: 'Quarter to date',
  ytd: 'Year to date',
  last_year: 'Last year',
  all_time: 'All time',
  custom: 'Custom range',
};

export const DATE_FIELD_LABELS: Record<ReportDateField, string> = {
  close: 'Closed (by close date)',
  contract: 'Written (by contract date)',
  list: 'Listings taken (by list date)',
};

export const COMPARE_LABELS: Record<ReportCompare, string> = {
  none: 'No comparison',
  previous: 'vs. previous period',
  year_ago: 'vs. same period last year',
};

export interface ReportRange {
  preset: ReportPreset;
  start: number | null;
  end: number | null;
  start_ymd: string | null;
  end_ymd: string | null;
  label: string;
}

export interface MoneyBucket { units: number; volume_cents: number }

export interface ReportSummary {
  units: number;
  volume_cents: number;
  avg_price_cents: number | null;
  median_price_cents: number | null;
  mix: { listing: MoneyBucket; buyer: MoneyBucket; auction: MoneyBucket };
  listing_to_buyer_ratio: number | null;
  gross_commission_cents: number | null;
  gci_cents: number | null;
  net_to_company_cents: number | null;
  agent_commission_cents: number | null;
  /** Showing assistants and partners, paid out of the agents' side. */
  partner_commission_cents: number | null;
  estimated_commission_deals: number;
  avg_days_list_to_contract: number | null;
  median_days_list_to_contract: number | null;
  avg_days_contract_to_close: number | null;
  median_days_contract_to_close: number | null;
  avg_days_list_to_close: number | null;
  list_to_sale_ratio: number | null;
  closed_units: number;
  closed_volume_cents: number;
  contracts_written: number;
  listings_taken: number;
  listings_sold: number;
  fell_through: number;
  fall_through_rate: number | null;
}

export interface BreakdownRow {
  key: string;
  label: string;
  units: number;
  volume_cents: number;
  avg_price_cents: number | null;
  gci_cents: number | null;
  net_to_company_cents: number | null;
  /** Build 87: the agents' side of the commission. */
  agent_cents?: number | null;
  /** Showing assistants and partners. */
  partner_cents?: number | null;
  drill: Record<string, string> | null;
}

export interface ReportChecks {
  both_sides: Array<{ address: string; ids: number[] }>;
  not_counted: Array<{ id: number; address: string; reason: 'closed_no_date' | 'past_due' | 'deleted_closed'; date: string | null }>;
  da: { closed: number; verified: number; unverified: Array<{ id: number; address: string; date: string | null }> } | null;
  kw: {
    paid_cents: number;
    verified_cents: number;
    fees: { eo_cents: number; royalty_cents: number; company_dollar_cents: number; total_cents: number; deals: number };
  } | null;
}

export interface DealsReport {
  range: ReportRange & { date_field: ReportDateField };
  compare: ReportRange | null;
  scope: 'team' | 'own';
  commission_visible: boolean;
  per_agent_visible: boolean;
  current: ReportSummary;
  previous: ReportSummary | null;
  checks: ReportChecks;
  breakdowns: {
    by_agent: BreakdownRow[];
    by_type: BreakdownRow[];
    by_city: BreakdownRow[];
    by_price_band: BreakdownRow[];
    by_lead_source: BreakdownRow[];
    by_month: BreakdownRow[];
  };
}

export interface DealsReportQuery {
  preset: ReportPreset;
  from?: string;
  to?: string;
  date_field: ReportDateField;
  compare: ReportCompare;
  type?: TxType | '';
  agent?: string;
  /** Build 20: '1' also counts deals where the agent is a co-agent. */
  co_agent?: string;
  city?: string;
  zip?: string;
}

export function reportQueryString(q: DealsReportQuery): string {
  const p = new URLSearchParams();
  p.set('preset', q.preset);
  if (q.preset === 'custom') {
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
  }
  p.set('date_field', q.date_field);
  p.set('compare', q.compare);
  if (q.type) p.set('type', q.type);
  if (q.agent) p.set('agent', q.agent);
  if (q.agent && q.co_agent === '1') p.set('co_agent', '1');
  if (q.city) p.set('city', q.city);
  if (q.zip) p.set('zip', q.zip);
  return p.toString();
}

export function getDealsReport(q: DealsReportQuery): Promise<DealsReport> {
  return apiFetch(`/api/reports/deals?${reportQueryString(q)}`);
}

export async function downloadDealsReportCsv(q: DealsReportQuery): Promise<void> {
  const { getToken } = await import('../auth/tokenStore');
  const token = getToken();
  if (!token) throw new Error('Not signed in');
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${apiBase}/api/reports/deals/export.csv?${reportQueryString(q)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed (HTTP ${res.status}). ${text.slice(0, 200)}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `deals-report-${q.preset}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Build 92: the deals behind any number ─────────────────────────────────
export type ReportEventMetric = 'closed' | 'contracts_written' | 'listings_taken' | 'listings_sold' | 'fell_through';
export type ReportPrimaryMetric =
  | 'deals' | 'listing' | 'auction' | 'buyer' | 'estimated'
  | 'list_to_contract' | 'contract_to_close' | 'list_to_close' | 'sale_to_list';
export type ReportDealsMetric = ReportPrimaryMetric | ReportEventMetric;
export type ReportBreakdownKey = 'by_agent' | 'by_type' | 'by_city' | 'by_price_band' | 'by_lead_source' | 'by_month';

export const METRIC_LABELS: Record<ReportDealsMetric, string> = {
  deals: 'Deals',
  listing: 'Listings',
  auction: 'Auctions',
  buyer: 'Buyer sides',
  estimated: 'Deals with no commission entered',
  list_to_contract: 'Deals in the list to contract average',
  contract_to_close: 'Deals in the contract to close average',
  list_to_close: 'Deals in the list to close average',
  sale_to_list: 'Closed listings in the sale vs. list figure',
  closed: 'Closed',
  contracts_written: 'Contracts written',
  listings_taken: 'Listings taken',
  listings_sold: 'Listings sold',
  fell_through: 'Fell through',
};

export interface ReportDrill {
  metric: ReportDealsMetric;
  period?: 'current' | 'previous';
  group?: ReportBreakdownKey;
  key?: string;
}

export interface ReportDealRow {
  id: number;
  address: string;
  city: string | null;
  type: TxType;
  status: string;
  agent: string | null;
  list_price_cents: number | null;
  sale_price_cents: number | null;
  price_cents: number;
  list_date: string | null;
  contract_date: string | null;
  close_date: string | null;
  days_list_to_contract: number | null;
  days_contract_to_close: number | null;
  lead_source: string | null;
  dead_reason: string | null;
  gci_cents: number | null;
  net_to_company_cents: number | null;
  agent_cents: number | null;
  commission_estimated: boolean;
}

export interface ReportDeals {
  range: ReportRange;
  period: 'current' | 'previous';
  metric: ReportDealsMetric;
  group: { key: ReportBreakdownKey; value: string; label: string | null } | null;
  commission_visible: boolean;
  count: number;
  volume_cents: number;
  gci_cents: number | null;
  net_to_company_cents: number | null;
  agent_cents: number | null;
  deals: ReportDealRow[];
}

function drillParams(q: DealsReportQuery, d: ReportDrill): URLSearchParams {
  const p = new URLSearchParams(reportQueryString(q));
  p.set('metric', d.metric);
  if (d.period === 'previous') p.set('period', 'previous');
  if (d.group) { p.set('group', d.group); p.set('key', d.key ?? ''); }
  return p;
}

export function getReportDeals(q: DealsReportQuery, d: ReportDrill): Promise<ReportDeals> {
  return apiFetch(`/api/reports/deals/behind?${drillParams(q, d).toString()}`);
}

/** The in-app link for one number on the report. */
export function reportDealsHref(q: DealsReportQuery, d: ReportDrill): string {
  return `/reports/deals?${drillParams(q, d).toString()}`;
}

// ── Dashboard: this month vs last month ────────────────────────────────────
export interface MonthBucket {
  label: string;
  start_ymd: string;
  end_ymd: string;
  closed_units: number;
  closed_volume_cents: number;
  contracts_written: number;
  listings_taken: number;
  new_leads: number;
  appointments_set: number;
  net_to_company_cents: number | null;
}

export interface MonthCompare {
  scope: 'team' | 'own';
  commission_visible: boolean;
  day_of_month: number;
  this_month: MonthBucket;
  last_month_to_date: MonthBucket;
  last_month_full: MonthBucket;
  year_ago_to_date: MonthBucket;
  year_ago_full: MonthBucket;
  pace: {
    projected_units: number;
    projected_volume_cents: number;
    scheduled_units: number;
    scheduled_volume_cents: number;
    projected_net_to_company_cents: number | null;
    days_left: number;
  };
}

export function getMonthCompare(): Promise<MonthCompare> {
  return apiFetch('/api/reports/month-compare');
}

// ── Monthly recaps (owner / operations) ────────────────────────────────────
export interface RecapListItem {
  id: number;
  team_id: number;
  month_key: string;
  subject: string;
  status: string;
  built_at: number;
}

export interface Recap extends RecapListItem {
  body_text: string;
  data_json: string;
  data: unknown;
}

export function listRecaps(): Promise<{ recaps: RecapListItem[] }> {
  return apiFetch('/api/reports/recaps');
}

export function getRecap(month: string): Promise<{ recap: Recap }> {
  return apiFetch(`/api/reports/recaps/${encodeURIComponent(month)}`);
}

export function buildRecap(month?: string): Promise<{ recap: Recap }> {
  return apiFetch('/api/reports/recaps/build', {
    method: 'POST',
    body: JSON.stringify(month ? { month } : {}),
  });
}

// ── Comp ledger (Build 31, Daniel 2026-09-20) ────────────────────────
//
// Two answers for every closed side: what TR paid, and what the comp plan
// says. Read only. See apps/api/src/lib/comp-ledger.ts for why the plan is
// not (yet) the thing that writes payouts.

export interface CompLedgerSide {
  transaction_id: number;
  close_date: number | null;
  address: string;
  type: string;
  side: 'listing' | 'buyer';
  sale_price_cents: number;
  /** The side after referral and co-op. Both answers divide this. */
  gci_cents: number;
  lead_origin: 'self' | 'team' | 'off_market_no_team' | null;
  is_flip: boolean;
  showing_assisted: boolean;
  paid_agent_cents: number;
  paid_showing_cents: number;
  paid_house_cents: number;
  paid_agent_pct: number;
  /** Null when the lead origin is unset, because the answer depends on it. */
  plan_agent_cents: number | null;
  plan_showing_cents: number | null;
  plan_house_cents: number | null;
  plan_agent_pct: number | null;
  plan_tier: 1 | 2 | 3 | null;
  plan_notes: string[];
  /** Plan minus paid. Positive means the plan pays the agent more. */
  delta_agent_cents: number | null;
}

export interface CompLedger {
  user_id: number;
  name: string;
  window_start: number;
  window_end: number;
  anniversary_md: string | null;
  used_calendar_year: boolean;
  /** Which year the ladder counts in, as set on the Team page. */
  tier_year_basis?: 'calendar' | 'anniversary';
  sides: CompLedgerSide[];
  totals: {
    sides: number;
    /** Every total here EXCLUDES these, and the page says so. */
    sides_without_origin: number;
    gci_cents: number;
    paid_agent_cents: number;
    paid_showing_cents: number;
    paid_house_cents: number;
    plan_agent_cents: number;
    plan_showing_cents: number;
    plan_house_cents: number;
    delta_agent_cents: number;
    tier_counts: Record<'1' | '2' | '3', number>;
  };
}

export function getCompLedger(userId?: number): Promise<CompLedger> {
  return apiFetch(`/api/reports/comp-ledger${userId ? `?user_id=${userId}` : ''}`);
}

// ── Build 87: agent reports (month or quarter, one agent) ─────────────────
export interface AgentReport {
  subject: string;
  html: string;
  text: string;
  agent: { id: number; name: string; email: string };
  summary: { units_closed: number; volume_cents: number; gci_cents: number; paid_cents: number; gci_missing: number } | null;
}
export function getAgentReport(userId: number, period: string): Promise<AgentReport> {
  return apiFetch(`/api/production/agent-recap/render?user_id=${userId}&period=${encodeURIComponent(period)}`);
}
export function sendAgentReports(body: { user_ids?: number[]; all?: boolean; period: string; test_to_me?: boolean }): Promise<{ ok: boolean; sent: number; failed: number; sent_to?: string; test: boolean }> {
  return apiFetch('/api/production/agent-recap/send', { method: 'POST', body: JSON.stringify(body) });
}
export async function downloadAgentReportCsv(userId: number, period: string): Promise<void> {
  const { getToken } = await import('../auth/tokenStore');
  const token = getToken();
  if (!token) throw new Error('Not signed in');
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${apiBase}/api/production/agent-recap/deals.csv?user_id=${userId}&period=${encodeURIComponent(period)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Download failed (HTTP ${res.status}). ${text.slice(0, 200)}`);
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? `agent-report-${period}.csv`;
  saveBlob(await res.blob(), name);
}

/** Build 87: hand the browser a file. Shared by every report download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Build 89: data check ─────────────────────────────────────────────────
export interface DataCheckItem {
  id: number; type: string; status: string; address: string; agent: string | null;
  close_date: number | null; list_price: number | null; sale_price: number | null; note: string; group?: string;
}
export interface DataCheck {
  year: number;
  closed_in_year: number;
  summaries_on_file: number;
  commission_on_file: number;
  checks: Array<{ key: string; title: string; why: string; fix: string; count: number; items: DataCheckItem[] }>;
}
export function getDataCheck(year: number): Promise<DataCheck> {
  return apiFetch(`/api/reports/data-check?year=${year}`);
}

// ── Pipeline (Daniel 2026-10-09): every deal by stage ─────────────────
export type PipelineStage = 'active' | 'pending' | 'withdrawn' | 'expired' | 'cancelled' | 'closed';
export interface PipelineDeal {
  id: number;
  address: string;
  city: string | null;
  type: TxType;
  stage: PipelineStage;
  agent: string | null;
  list_price_cents: number | null;
  price_cents: number;
  list_date: string | null;
  contract_date: string | null;
  close_date: string | null;
  days_on_market: number | null;
  flag: string | null;
}
export interface ListingPipeline {
  scope: 'team' | 'own';
  counts: Record<PipelineStage, { units: number; volume_cents: number }>;
  total: number;
  deals: PipelineDeal[];
  flags: { key: string; label: string; count: number }[];
}
export function getListingPipeline(type: string, agent: string): Promise<ListingPipeline> {
  const p = new URLSearchParams();
  if (type) p.set('type', type);
  if (agent) p.set('agent', agent);
  return apiFetch(`/api/reports/pipeline?${p.toString()}`);
}
